import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyPageMeta, removeHreflang } from '../../helpers/seoMeta';

const SEO = ({
  title = "Terserah - Indonesia's #1 Marketplace",
  description = "Buy and sell products on Indonesia's leading marketplace. Electronics, fashion, home, vehicles and more. Post for free!",
  keywords = 'marketplace indonesia, buy sell indonesia, classified ads indonesia, second hand indonesia',
  image = 'https://terserah.id/og-image.jpg',
  type = 'website',
  article = null, // For article schema
  breadcrumbs = [], // Array of { name, url }
}) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const url = `https://terserah.id${location.pathname}`;
  const currentLang = i18n.language || 'en';

  useEffect(() => {
    // title, description, canonical, OG, Twitter y robots en un solo sitio.
    // applyPageMeta normaliza el canonical sin query string y garantiza que
    // og:image/twitter:image sea JPG/PNG (WebP no es fiable en previews).
    removeHreflang();
    applyPageMeta({
      title,
      description,
      keywords,
      image,
      type,
      path: location.pathname,
      lang: currentLang,
    });

    // Remove old structured data
    const oldScripts = document.querySelectorAll('script[type="application/ld+json"]');
    oldScripts.forEach((script) => {
      if (script.dataset.dynamic === 'true') {
        script.remove();
      }
    });

    // Add breadcrumbs schema if provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: `https://terserah.id${crumb.url}`,
        })),
      };

      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.dataset.dynamic = 'true';
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(breadcrumbScript);
    }

    // Add article schema if provided
    if (article) {
      const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: article.title,
        description: article.description,
        image: article.image ? [article.image] : [],
        brand: article.brand || 'Terserah',
        sku: article.public_id,
        mpn: article.public_id,
        offers: {
          '@type': 'Offer',
          url: url,
          priceCurrency: 'IDR',
          price: article.price,
          priceValidUntil:
            article.priceValidUntil ||
            new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          availability: article.sold
            ? 'https://schema.org/OutOfStock'
            : 'https://schema.org/InStock',
          itemCondition: article.condition
            ? article.condition === 'new'
              ? 'https://schema.org/NewCondition'
              : article.condition === 'like_new'
                ? 'https://schema.org/RefurbishedCondition'
                : 'https://schema.org/UsedCondition'
            : 'https://schema.org/UsedCondition',
        },
      };

      if (article.seller) {
        articleSchema.offers.seller = {
          '@type': 'Person',
          name: article.seller,
        };
      }

      if (article.rating && article.reviewCount) {
        articleSchema.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: article.rating,
          reviewCount: article.reviewCount,
          bestRating: '5',
          worstRating: '1',
        };
      }

      if (article.category) {
        articleSchema.category = article.category;
      }

      const articleScript = document.createElement('script');
      articleScript.type = 'application/ld+json';
      articleScript.dataset.dynamic = 'true';
      articleScript.textContent = JSON.stringify(articleSchema);
      document.head.appendChild(articleScript);
    }

    // Add Organization schema
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Terserah',
      url: 'https://terserah.id',
      logo: 'https://terserah.id/logo.png',
      description: "Indonesia's leading marketplace for buying and selling products",
      sameAs: [
        'https://facebook.com/terserah',
        'https://twitter.com/terserah',
        'https://instagram.com/terserah',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Service',
        availableLanguage: ['English', 'Indonesian', 'Spanish'],
      },
    };

    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.dataset.dynamic = 'true';
    orgScript.textContent = JSON.stringify(organizationSchema);
    document.head.appendChild(orgScript);
  }, [title, description, keywords, image, type, url, breadcrumbs, article, currentLang]);

  return null;
};

export default SEO;
